import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { FilePlus2, Eraser as Broom, BellRing, RefreshCw, Archive, Mail } from "lucide-react";

const fmt = (n) => `${Number(n || 0).toFixed(2)} $`;
const KIND = { dojo: "Dojo", petits_samourais: "Petits Samouraïs", powerkick_her: "PowerKick Her" };

function Panel({ icon: Icon, title, desc, children, testId }) {
  return (
    <div className="border border-white/10 bg-black/30 p-4 mb-4" data-testid={testId}>
      <h3 className="text-sm text-white font-semibold flex items-center gap-2"><Icon size={16} className="text-[#C9A961]" /> {title}</h3>
      <p className="text-[11px] text-white/50 mt-1 mb-3">{desc}</p>
      {children}
    </div>
  );
}

const Th = ({ children }) => <th className="text-left text-[11px] uppercase tracking-wider text-white/40 font-medium py-1.5 pr-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-1.5 pr-3 text-xs text-white/80 align-top ${className}`}>{children}</td>;

// ── 1. Contrats manquants en 1 clic ──
export function MissingContractsPanel({ onDone }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stripe-audit/missing-contracts");
      setData(data);
      setSel(new Set(data.rows.filter((r) => !r.warning && r.full_name).map((r) => r.key)));
    } catch (e) {
      setData({ rows: [], count: 0, error: e?.response?.data?.detail });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (k) => setSel((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const create = async () => {
    const keys = [...sel];
    if (!keys.length) return;
    const rows = data.rows.filter((r) => keys.includes(r.key));
    const txt = rows.map((r) => `• ${r.full_name} — ${KIND[r.kind]} — ${fmt(r.tarif_mensuel)}`).join("\n");
    if (!window.confirm(`Créer ${keys.length} contrat(s) élève pré-remplis depuis Stripe ?\n\n${txt}\n\nStatut « actif », source stripe_audit, marqués « à vérifier ». Aucune écriture Stripe.`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/stripe-audit/missing-contracts/create?confirm=true", { keys }, { timeout: 60000 });
      toast.success(`${res.created.length} contrat(s) créé(s)${res.skipped.length ? ` · ${res.skipped.length} ignoré(s)` : ""}`);
      load();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel icon={FilePlus2} title={`Contrats manquants en 1 clic (${data?.count ?? "…"})`} testId="missing-contracts-panel"
      desc="Abonnements Stripe dojo / Petits Samouraïs sans contrat élève et paiements PowerKick Her sans fiche : crée les contrats pré-remplis (nom, courriel, montant, client et abonnement Stripe reliés).">
      {data?.error && <p className="text-xs text-amber-300" data-testid="missing-contracts-error">{data.error}</p>}
      {data && !data.error && data.count === 0 && <p className="text-xs text-emerald-300" data-testid="missing-contracts-empty">Aucun contrat manquant ✅</p>}
      {data?.rows?.length > 0 && (
        <>
          <div className="overflow-x-auto">
            <table className="w-full"><thead><tr><Th></Th><Th>Nom</Th><Th>Type</Th><Th>Courriel</Th><Th>Montant</Th><Th>Stripe</Th><Th>Alerte</Th></tr></thead>
              <tbody>{data.rows.map((r) => (
                <tr key={r.key} className="border-t border-white/5" data-testid={`missing-row-${r.key}`}>
                  <Td><input type="checkbox" checked={sel.has(r.key)} onChange={() => toggle(r.key)} data-testid={`missing-check-${r.key}`} /></Td>
                  <Td>{r.full_name || <i className="text-amber-300">sans nom</i>}</Td>
                  <Td>{KIND[r.kind]}</Td>
                  <Td>{r.email || "—"}</Td>
                  <Td>{fmt(r.tarif_mensuel)} / {r.billing_cycle}</Td>
                  <Td className="font-mono">{r.stripe_subscription_id || r.key}<br /><span className="text-white/40">{r.stripe_status}</span></Td>
                  <Td>{r.warning && <span className="text-amber-300">⚠ {r.warning}</span>}</Td>
                </tr>))}</tbody></table>
          </div>
          <button onClick={create} disabled={busy || !sel.size} data-testid="missing-contracts-create-btn"
            className="mt-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-medium px-4 py-2 flex items-center gap-2">
            <FilePlus2 size={15} /> Créer {sel.size} contrat(s) en 1 clic
          </button>
        </>
      )}
    </Panel>
  );
}

// ── 2. Nettoyage Stripe (≥ 90 jours) ──
export function CleanupPanel() {
  const [state, setState] = useState(null);
  const [selL, setSelL] = useState(new Set());
  const [selP, setSelP] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/stripe-audit/cleanup/preview");
      setState(data);
      if (data.status === "ready") {
        setSelL(new Set((data.links || []).filter((l) => !l.protected).map((l) => l.id)));
        setSelP(new Set((data.products || []).filter((p) => !p.protected).map((p) => p.id)));
      }
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (state?.status !== "running") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [state?.status, load]);

  const run = async () => {
    try {
      await api.post("/stripe-audit/cleanup/preview");
      setState((s) => ({ ...(s || {}), status: "running" }));
      toast.info("Analyse Stripe en cours (liens, produits, usage 90 jours)…");
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  const archive = async () => {
    const link_ids = [...selL], product_ids = [...selP];
    if (!link_ids.length && !product_ids.length) return;
    if (!window.confirm(`Archiver dans Stripe :\n• ${link_ids.length} lien(s) de paiement\n• ${product_ids.length} produit(s) (et leurs prix)\n\nInutilisés depuis ${state.days} jours. Réversible depuis le tableau de bord Stripe (réactiver). Aucun paiement ni abonnement existant n'est touché.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/stripe-audit/cleanup/archive?confirm=true", { link_ids, product_ids }, { timeout: 120000 });
      toast.success(`${data.done.length} élément(s) archivé(s)${data.errors.length ? ` · ${data.errors.length} erreur(s)` : ""}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };

  const tog = (setter) => (id) => setter((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const running = state?.status === "running";
  const ready = state?.status === "ready";

  return (
    <Panel icon={Broom} title="Nettoyage Stripe — liens & produits inutilisés depuis 90 jours" testId="cleanup-panel"
      desc="Aperçu d'abord, archivage seulement après ta confirmation. Les éléments encore référencés par la plateforme sont protégés (non cochables).">
      <div className="flex gap-2 flex-wrap mb-3">
        <button onClick={run} disabled={running} data-testid="cleanup-preview-btn" className="border border-white/20 hover:bg-white/10 disabled:opacity-50 text-white/80 text-sm px-3 py-2 flex items-center gap-2">
          <RefreshCw size={14} className={running ? "animate-spin" : ""} /> {running ? "Analyse…" : ready ? "Rafraîchir l'aperçu" : "Générer l'aperçu"}
        </button>
        {ready && (selL.size + selP.size > 0) && (
          <button onClick={archive} disabled={busy} data-testid="cleanup-archive-btn" className="bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-50 text-white text-sm font-medium px-4 py-2 flex items-center gap-2">
            <Archive size={15} /> Archiver {selL.size + selP.size} élément(s)
          </button>
        )}
      </div>
      {state?.status === "error" && <p className="text-xs text-red-300" data-testid="cleanup-error">Échec : {state.error}</p>}
      {ready && (
        <>
          <p className="text-[11px] text-white/40 mb-2" data-testid="cleanup-meta">Aperçu du {state.at?.replace("T", " ").slice(0, 16)} UTC · {state.stats?.used_links} liens et {state.stats?.used_prices} prix utilisés sur 90 j · {state.stats?.platform_refs} références plateforme protégées</p>
          <p className="text-xs text-white/70 font-medium mt-2">Liens de paiement inutilisés ({state.links.length})</p>
          {state.links.length === 0 ? <p className="text-xs text-emerald-300">Aucun ✅</p> : (
            <table className="w-full mb-3"><tbody>{state.links.map((l) => (
              <tr key={l.id} className="border-t border-white/5" data-testid={`cleanup-link-${l.id}`}>
                <Td><input type="checkbox" disabled={l.protected} checked={selL.has(l.id)} onChange={() => tog(setSelL)(l.id)} /></Td>
                <Td>{l.label}{l.protected && <span className="ml-2 text-sky-300">🔒 utilisé par la plateforme</span>}</Td>
                <Td className="font-mono">{l.id}</Td>
              </tr>))}</tbody></table>
          )}
          <p className="text-xs text-white/70 font-medium mt-2">Produits inutilisés ({state.products.length})</p>
          {state.products.length === 0 ? <p className="text-xs text-emerald-300">Aucun ✅</p> : (
            <table className="w-full"><tbody>{state.products.map((p) => (
              <tr key={p.id} className="border-t border-white/5" data-testid={`cleanup-product-${p.id}`}>
                <Td><input type="checkbox" disabled={p.protected} checked={selP.has(p.id)} onChange={() => tog(setSelP)(p.id)} /></Td>
                <Td>{p.name}{p.protected && <span className="ml-2 text-sky-300">🔒 utilisé par la plateforme</span>}<br /><span className="text-white/40">créé le {p.created} · {p.prices.map((x) => `${fmt(x.amount)}${x.recurring ? "/réc." : ""}`).join(", ") || "aucun prix actif"}</span></Td>
                <Td className="font-mono">{p.id}</Td>
              </tr>))}</tbody></table>
          )}
        </>
      )}
      {state?.status === "none" && <p className="text-xs text-white/40" data-testid="cleanup-empty">Aucun aperçu généré.</p>}
    </Panel>
  );
}

// ── 3. Alerte hebdo « fuites » ──
export function WeeklyLeaksPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showHtml, setShowHtml] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/stripe-audit/leaks/preview"); setData(data); }
    catch (e) { setData({ error: e?.response?.data?.detail || "Aperçu indisponible" }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    const T = data?.totals || {};
    if (!window.confirm(`Envoyer MAINTENANT le résumé des fuites à ${data?.config?.recipient} ?\n• ${T.active_no_sub} actifs sans abonnement\n• ${T.declined} cartes refusées\n• ${T.paused} en pause`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/stripe-audit/leaks/send-now?confirm=true", {}, { timeout: 60000 });
      toast.success(r.sent ? `Résumé envoyé à ${r.to}` : "Envoi non confirmé");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); } finally { setBusy(false); }
  };

  const toggle = async () => {
    try {
      await api.put("/stripe-audit/leaks/config", { enabled: !data.config.enabled, recipient: data.config.recipient });
      toast.success(!data.config.enabled ? "Alerte du lundi ACTIVÉE" : "Alerte du lundi désactivée");
      load();
    } catch { toast.error("Réglage non enregistré"); }
  };

  const T = data?.totals;
  return (
    <Panel icon={BellRing} title="Alerte hebdo « fuites » — chaque lundi" testId="weekly-leaks-panel"
      desc="Courriel automatique au propriétaire : élèves actifs sans abonnement, cartes refusées (past_due / unpaid), abonnements en pause et échecs de paiement des 7 derniers jours. Signale seulement — n'agit jamais.">
      {data?.error && <p className="text-xs text-amber-300" data-testid="weekly-leaks-error">{data.error}</p>}
      {T && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[["Actifs sans abonnement", `${T.active_no_sub} · ≈ ${fmt(T.lost_monthly)}/mois`, "kpi-leaks-nosub"], ["Cartes refusées", `${T.declined} · ${fmt(T.declined_amount)}`, "kpi-leaks-declined"],
              ["En pause", `${T.paused} · ${fmt(T.paused_amount)}`, "kpi-leaks-paused"], ["Échecs 7 j", T.failed_events_7d, "kpi-leaks-failed"]].map(([l, v, id]) => (
              <div key={id} className="border border-white/10 bg-black/20 p-2" data-testid={id}><p className="text-[10px] uppercase tracking-wider text-white/40">{l}</p><p className="text-sm text-white font-semibold">{v}</p></div>))}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <button onClick={toggle} data-testid="weekly-leaks-toggle" className={`text-xs px-3 py-1.5 border ${data.config.enabled ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" : "border-amber-500/40 text-amber-400 bg-amber-500/10"}`}>
              Lundi 8 h : {data.config.enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}
            </button>
            <button onClick={send} disabled={busy} data-testid="weekly-leaks-send-btn" className="border border-white/20 hover:bg-white/10 disabled:opacity-50 text-white/80 text-xs px-3 py-1.5 flex items-center gap-2"><Mail size={13} /> Envoyer maintenant</button>
            <button onClick={() => setShowHtml((v) => !v)} data-testid="weekly-leaks-preview-toggle" className="text-xs text-white/60 underline">{showHtml ? "Masquer l'aperçu" : "Aperçu du courriel"}</button>
            <span className="text-[11px] text-white/40">→ {data.config.recipient}{data.config.last_run?.at ? ` · dernier envoi ${data.config.last_run.at.slice(0, 10)}` : ""}</span>
          </div>
          {showHtml && <div className="mt-3 bg-white p-3 text-black text-xs" data-testid="weekly-leaks-html" dangerouslySetInnerHTML={{ __html: data.html }} />}
        </>
      )}
    </Panel>
  );
}

export default function AuditActions({ onChanged }) {
  return (
    <div className="mt-6" data-testid="audit-actions">
      <MissingContractsPanel onDone={onChanged} />
      <CleanupPanel />
      <WeeklyLeaksPanel />
    </div>
  );
}
